import { useState } from "react";
import { toast } from "sonner";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { SectionHead } from "../components/SectionHead";
import { TALENT_CATEGORIES, IMG } from "../data/content";
import { api, track } from "../lib/api";

const initial = {
  name: "", contact: "", event_date: "", city: "", event_type: "",
  talent_category: "", language: "", appearance_type: "", duration: "",
  budget: "", hospitality_info: "", website: "",
};

export default function Talent() {
  const [form, setForm] = useState(initial);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    track("talent_enquiry_started", {});
    try {
      await api.post("/talent-enquiries", form);
      track("talent_enquiry_submitted", { category: form.talent_category });
      setSent(true);
    } catch {
      toast.error("Something went wrong. Please try WhatsApp or call us.");
    }
  };

  return (
    <main>
      <PageHero
        eyebrow="Celebrity & Talent"
        title="The Right Talent. The Right Occasion. The Right Experience."
        description="From selecting the right personality to coordinating schedules, hospitality and appearance flow — every engagement is approached professionally and confidentially."
        image={IMG.dinnerEvent}
      />

      <section className="py-20 lg:py-28" data-testid="talent-categories-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <SectionHead eyebrow="Talent Categories" title="Who Should Walk Into Your Event?" />
          <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-px bg-[#C9A55C]/12 border border-[#C9A55C]/12">
            {TALENT_CATEGORIES.map((c, i) => (
              <Reveal key={c} delay={(i % 5) * 0.05}>
                <div className="bg-[#0D1018] p-6 h-full hover:bg-[#11141f] transition-colors duration-500">
                  <p className="font-accent italic text-[#C9A55C]/60 text-xl">{String(i + 1).padStart(2, "0")}</p>
                  <p className="mt-3 font-display text-base text-[#F3EEE5]">{c}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <p className="mt-8 text-xs text-[#A9ABB2]/70 max-w-2xl leading-relaxed" data-testid="talent-disclaimer">
              Talent availability, fees, travel, hospitality, technical riders, appearance conditions and commercial terms are subject to direct confirmation and contract. No artist is presented as available without confirmation.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-[#0D1018] border-t border-[#C9A55C]/10" data-testid="talent-enquiry-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-8">
          <SectionHead eyebrow="Talent Enquiry" title="Tell Us Who You Have in Mind" align="center" />
          <Reveal delay={0.15} className="mt-12">
            {sent ? (
              <div className="border border-[#C9A55C]/30 p-10 text-center" data-testid="talent-success-message">
                <p className="font-display text-2xl text-[#C9A55C] mb-4">Thank you.</p>
                <p className="text-[#A9ABB2] text-sm">Our talent coordination team will review your enquiry and respond confidentially.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="grid sm:grid-cols-2 gap-5" data-testid="talent-enquiry-form">
                <input className="lux-input" placeholder="Your Name *" required value={form.name} onChange={set("name")} data-testid="talent-name-input" />
                <input className="lux-input" placeholder="Mobile / Email *" required value={form.contact} onChange={set("contact")} data-testid="talent-contact-input" />
                <input className="lux-input" type="date" value={form.event_date} onChange={set("event_date")} data-testid="talent-date-input" />
                <input className="lux-input" placeholder="City" value={form.city} onChange={set("city")} data-testid="talent-city-input" />
                <input className="lux-input" placeholder="Event Type" value={form.event_type} onChange={set("event_type")} data-testid="talent-event-type-input" />
                <select className="lux-input" value={form.talent_category} onChange={set("talent_category")} data-testid="talent-category-select">
                  <option value="">Preferred Talent Category</option>
                  {TALENT_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                </select>
                <select className="lux-input" value={form.language} onChange={set("language")} data-testid="talent-language-select">
                  <option value="">Preferred Language</option>
                  <option>Telugu</option><option>Hindi</option><option>English</option><option>Tamil</option><option>Other</option>
                </select>
                <select className="lux-input" value={form.appearance_type} onChange={set("appearance_type")} data-testid="talent-appearance-select">
                  <option value="">Appearance or Performance?</option>
                  <option>Appearance</option><option>Performance</option><option>Both</option>
                </select>
                <input className="lux-input" placeholder="Performance Duration" value={form.duration} onChange={set("duration")} data-testid="talent-duration-input" />
                <select className="lux-input" value={form.budget} onChange={set("budget")} data-testid="talent-budget-select">
                  <option value="">Budget Range</option>
                  <option>Under ₹5 Lakhs</option><option>₹5 – 15 Lakhs</option><option>₹15 – 50 Lakhs</option><option>₹50 Lakhs+</option>
                </select>
                <textarea className="lux-input sm:col-span-2 min-h-[100px]" placeholder="Travel & hospitality information" value={form.hospitality_info} onChange={set("hospitality_info")} data-testid="talent-hospitality-textarea" />
                <input type="text" value={form.website} onChange={set("website")} className="hidden" tabIndex="-1" autoComplete="off" />
                <button type="submit" className="btn-gold sm:col-span-2 justify-center" data-testid="talent-enquiry-submit-btn">
                  Submit Talent Enquiry
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </main>
  );
}
